import type { Scan, ScpData } from "@hobbyshopify/backend-client";
import { useState } from "react";
import { estimateForGrade, type Grade, gradeLabel, gradePriceCents } from "../core/primitives/grades";
import { type EditState, isFloorValid, isYearValid, MAX_YEAR, MIN_YEAR } from "../core/primitives/scan-edit";
import { GradeCombobox } from "./GradeCombobox";

/**
 * The confirm form: correct what the model read, then say what it is worth and
 * whether you are stocking it.
 *
 * The fields above the fold are the ones that decide **which card this is** —
 * they are the identity a `Card` is keyed on, so editing one of them here is
 * what makes a corrected scan attach to the right card (or create a new one)
 * instead of quietly joining the wrong one's price history. Everything else is
 * description, and lives under the "Player, set, year and number" toggle.
 */

const CARD_TYPES = [
  { value: "sports", label: "Sports" },
  { value: "pokemon", label: "Pokémon" },
  { value: "mtg", label: "Magic: The Gathering" },
  { value: "yugioh", label: "Yu-Gi-Oh!" },
  { value: "comic", label: "Comics" },
];

/** The shared `.field` primitive from index.css. */
export const inputCls = "field";

function FieldRow({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 block text-xs font-medium text-muted">
        {label}
        {required && <span className="ml-0.5 text-danger">*</span>}
      </div>
      {children}
    </div>
  );
}

export function ScanEditForm({
  editState,
  onChange,
  matchedCard,
  noEstimate,
  estimatedFrom,
  scpData,
}: {
  editState: EditState;
  onChange: (updates: Partial<EditState>) => void;
  // The card the matcher proposed, when it proposed one. Shown so the reviewer
  // can see what they are about to add a copy to.
  matchedCard?: Scan["card"];
  /** True when the lookup could not price this card at its grade. */
  noEstimate?: boolean;
  /** The grade the prefilled estimate came from, when it is not this card's. */
  estimatedFrom?: string | null;
  /** The pricing payload, so the grade list can show what each grade is worth. */
  scpData?: ScpData | null;
}) {
  const set = (key: keyof EditState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    onChange({ [key]: e.target.value });

  const [showMore, setShowMore] = useState(false);
  /** The last automatic re-price, until the reviewer touches the price again. */
  const [repriced, setRepriced] = useState<{ previous: string | null; note: string } | null>(null);

  /**
   * The grade changed, so the price the old grade justified no longer applies.
   *
   * Re-estimating is the point: the grade *is* which comp the card is worth, so
   * correcting a PSA 4 reading to PSA 9 and leaving $8.50 in the field is how a
   * $830 card gets sold for the price of a different one. It overwrites rather
   * than filling only a blank field, because the stale figure is exactly the
   * one that has to go — and it is undoable, and never silent, because the
   * overwritten number may have been a decision rather than an estimate.
   *
   * Picking from the list passes the figure the reviewer actually saw next to
   * the grade they clicked; a typed grade is estimated by the same rules the
   * backend prices by.
   */
  const applyGrade = (grade: string, slabbed: boolean, estimate = estimateForGrade(grade, slabbed, scpData)) => {
    const price = estimate.priceCents == null ? null : (estimate.priceCents / 100).toFixed(2);
    onChange({ grade, slabbed, ...(price == null ? {} : { price }) });

    if (price === editState.price) return setRepriced(null);
    if (price == null) {
      // The old figure is left standing: deleting it would replace a number
      // that is merely stale with no number at all, and the reviewer cannot
      // confirm what is not there.
      return setRepriced({
        previous: null,
        note: "No comps for this grade — check the asking price before confirming.",
      });
    }
    const from = estimate.approximatedFrom
      ? `${estimate.approximatedFrom} comps — this grade has none of its own`
      : `${gradeLabel(estimate.gradeKey ?? "")} comps`;
    setRepriced({ previous: editState.price, note: `Asking price re-estimated at $${price} from ${from}.` });
  };

  const pickGrade = (grade: Grade) => {
    const raw = grade.key === "raw";
    const cents = gradePriceCents(grade.key, scpData);
    applyGrade(
      raw ? "raw" : grade.label,
      !raw,
      // The list's own figure, so the number that lands in the field is the one
      // that was read next to the grade in it.
      cents == null ? undefined : { priceCents: cents, gradeKey: grade.key, approximatedFrom: null },
    );
  };

  const yearValid = isYearValid(editState.year);
  const floorValid = isFloorValid(editState);
  // An invalid year blocks confirming, so its field never hides behind the toggle.
  const detailsOpen = showMore || !yearValid;

  return (
    <div className="space-y-4">
      {matchedCard && (
        <div className="note note-accent text-accent">
          Already in your catalogue as{" "}
          <span className="font-semibold">{matchedCard.cardFullName ?? matchedCard.player}</span>
          {matchedCard.trackStock ? `, ${matchedCard.available} in stock` : ""}. Confirming adds this copy to it.
        </div>
      )}

      <FieldRow label="Card name">
        <input className={inputCls} value={editState.cardFullName} onChange={set("cardFullName")} />
        {/* Not part of the identity key — it is the one line a person reads the
            card by, which is exactly why it is the field that stays in view.
            The parts that actually key the card are under the toggle below. */}
        <p className="mt-1 text-xs text-faint">How this card reads in lists. Editing it does not change the match.</p>
      </FieldRow>

      <FieldRow label="Grade">
        <GradeCombobox
          value={editState.grade}
          onType={(grade) => onChange({ grade })}
          onCommit={(grade) => applyGrade(grade, editState.slabbed)}
          onPick={pickGrade}
          scpData={scpData}
          placeholder="PSA 10, 9.5, raw…"
        />
        <label className="mt-2 flex items-center gap-2 text-xs text-muted">
          <input
            type="checkbox"
            checked={editState.slabbed}
            // Slabbed decides whether an unparseable grade is a raw card or an
            // unpriceable one, so it re-prices for the same reason the grade does.
            onChange={(e) => applyGrade(editState.grade, e.target.checked)}
          />
          Slabbed
        </label>
        {/* The grade is part of the identity, so this is not cosmetic: a PSA 10
            and a raw copy are two cards with two price histories — and two
            prices, which is why the estimate above follows this field. */}
        <p className="mt-1 text-xs text-faint">
          Leave it empty for a raw card. A graded copy is a different card from a raw one, with its own price.
        </p>
      </FieldRow>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FieldRow label="Asking price">
          <input
            type="number"
            min="0"
            step="0.01"
            className={inputCls}
            value={editState.price}
            onChange={(e) => {
              setRepriced(null);
              onChange({ price: e.target.value });
            }}
          />
          {repriced ? (
            <p className="mt-1 text-xs text-warn">
              {repriced.note}{" "}
              {repriced.previous !== null && (
                <button
                  type="button"
                  className="underline"
                  onClick={() => {
                    onChange({ price: repriced.previous ?? "" });
                    setRepriced(null);
                  }}
                >
                  Undo
                </button>
              )}
            </p>
          ) : noEstimate ? (
            // Better than prefilling a number nobody computed — and better than
            // the raw price, which belongs to a different card.
            <p className="mt-1 text-xs text-warn">
              We couldn't price this grade automatically. Pick one from the comps below, or type a price.
            </p>
          ) : (
            estimatedFrom && (
              // The number is real but it is a neighbour's: SportsCardsPro has
              // no comps for BGS or half grades, so this reads off the grade
              // below. Saying which is the difference between an estimate and a
              // number presented as this card's own.
              <p className="mt-1 text-xs text-warn">
                Estimated from {estimatedFrom} comps — this grade has none of its own. Check the list below.
              </p>
            )
          )}
        </FieldRow>
        <FieldRow label="Floor price">
          <input
            type="number"
            min="0"
            step="0.01"
            className={`${inputCls} ${floorValid ? "" : "border-danger focus:border-danger"}`}
            value={editState.floorPrice}
            onChange={set("floorPrice")}
            aria-invalid={!floorValid}
          />
          {floorValid ? (
            <p className="mt-1 text-xs text-faint">
              Below this the shop does not sell. Leave it empty to review every customer offer by hand.
            </p>
          ) : (
            <p className="field-error text-xs">The floor cannot be above the asking price.</p>
          )}
        </FieldRow>
      </div>

      <button
        type="button"
        onClick={() => setShowMore((v) => !v)}
        aria-expanded={detailsOpen}
        className="flex w-full items-center justify-between rounded-btn border border-line px-3 py-2 text-sm font-medium text-white transition-colors hover:border-accent"
      >
        <span>Player, set, year and number</span>
        <span className="text-faint">{detailsOpen ? "▴" : "▾"}</span>
      </button>
      {detailsOpen && (
        <div className="space-y-4">
          {/* These are the fields the card is *keyed* on. Collapsed because the
              common case is agreeing with the reading; open when it is not. */}
          <p className="text-xs text-muted">
            Correcting anything here re-matches the scan, and may make it a different card.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FieldRow label="Player">
              <input className={inputCls} value={editState.player} onChange={set("player")} />
            </FieldRow>
            <FieldRow label="Team">
              <input className={inputCls} value={editState.team} onChange={set("team")} />
            </FieldRow>
            <FieldRow label="Brand">
              <input className={inputCls} value={editState.brand} onChange={set("brand")} />
            </FieldRow>
            <FieldRow label="Year">
              <input
                className={`${inputCls} ${yearValid ? "" : "border-danger focus:border-danger"}`}
                value={editState.year}
                onChange={(e) => onChange({ year: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                inputMode="numeric"
                aria-invalid={!yearValid}
              />
              {!yearValid && (
                <p className="field-error text-xs">
                  Enter a 4-digit year between {MIN_YEAR} and {MAX_YEAR}.
                </p>
              )}
            </FieldRow>
            <FieldRow label="Card #">
              <input className={inputCls} value={editState.cardNumber} onChange={set("cardNumber")} />
            </FieldRow>
            <FieldRow label="Parallel">
              <input className={inputCls} value={editState.parallelType} onChange={set("parallelType")} />
            </FieldRow>
            <FieldRow label="Insert">
              <input className={inputCls} value={editState.insertName} onChange={set("insertName")} />
            </FieldRow>
            <FieldRow label="Category">
              <select className={inputCls} value={editState.cardType} onChange={set("cardType")}>
                <option value="">Unspecified</option>
                {CARD_TYPES.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </FieldRow>
          </div>
          <FieldRow label="Set">
            <input className={inputCls} value={editState.setName} onChange={set("setName")} />
          </FieldRow>
        </div>
      )}
    </div>
  );
}
